/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React from "react";
import { TextField, Typography, Button } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { SimplicityComposerTipTap } from "cms/rich-text/simplicity-composer-tiptap";

const ProcessCardsComponentEditor = () => {
    const { component, update } = useUpdateComponent();
    
    const title = component?.title;
    const description = component?.description;
    const list = component?.list || [];

    const addListItem = () => {
        const newItem = {
            heading: "",
            description: "",
            stepNumber: ""
        };
        const updatedList = [...list, newItem];
        update("list", updatedList);
    };

    const removeListItem = (itemIndex) => {
        const isNotTargetItem = (_, currentIndex) => currentIndex !== itemIndex;
        const filteredList = list.filter(isNotTargetItem);
        update("list", filteredList);
    };

    const updateListItem = (itemIndex, fieldName, newValue) => {
        const updateTargetItem = (item, currentIndex) => {
            const isTargetItem = currentIndex === itemIndex;
            return isTargetItem ? { ...item, [fieldName]: newValue } : item;
        };
        const updatedList = list.map(updateTargetItem);
        console.log(`Updating item ${itemIndex}, field: ${fieldName}, value:`, newValue);
        console.log("Updated list:", updatedList);
        update("list", updatedList);
    };

    const handleStepNumberChange = (event, itemIndex) => {
        const inputValue = event.target.value;
        const numericValue = inputValue === "" ? "" : parseFloat(inputValue);
        updateListItem(itemIndex, "stepNumber", numericValue);
    };

    const handleHeadingChange = (event, itemIndex) => {
        const newHeading = event.target.value;
        updateListItem(itemIndex, "heading", newHeading);
    };

    const handleDescriptionChange = (newDescription, itemIndex) => {
        updateListItem(itemIndex, "description", newDescription);
    };

    return (
        <div className="simplicity-process-cards-editor">
            
            <div className="simplicity-process-cards-editor-general">
                <div className="simplicity-process-cards-editor-general-container">
                    <div className="simplicity-process-cards-editor-general-container-item">
                        <LabelledField label="Title">
                            <TextField
                                fullWidth
                                value={title || ""}
                                onChange={(event) => update("title", event.target.value)}
                                placeholder="Enter title"
                                variant="outlined"
                            />
                        </LabelledField>
                    </div>
                    
                    <div className="simplicity-process-cards-editor-general-container-item">
                        <LabelledField label="Description">
                            <SimplicityComposerTipTap
                                richtext={description}
                                onChange={(value) => update("description", value)}
                            />
                        </LabelledField>
                    </div>
                </div>
            </div>
            
            <div className="simplicity-process-cards-editor-add-item">
                <Button 
                    variant="outlined" 
                    onClick={addListItem}
                    color="primary"
                >
                    Add Process Card
                </Button>
            </div>

            {list.map((item, index) => {
                return (
                    <div key={index} className="simplicity-process-cards-editor-item">
                        <div className="simplicity-process-cards-editor-item-header">
                            <Typography variant="subtitle1">
                                Process Card {index + 1}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                size="small"
                                onClick={() => removeListItem(index)}
                            >
                                Remove
                            </Button>
                        </div>
                        
                        <div className="simplicity-process-cards-editor-item-fields">
                            <div className="simplicity-process-cards-editor-item-field">
                                <LabelledField label="Step Number">
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={item?.stepNumber || ""}
                                        onChange={(event) => handleStepNumberChange(event, index)}
                                        placeholder="Enter step number (e.g., 1, 2, 2.5, 3...)"
                                        variant="outlined"
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-process-cards-editor-item-field">
                                <LabelledField label="Card Heading">
                                    <TextField
                                        fullWidth
                                        value={item?.heading || ""}
                                        onChange={(event) => handleHeadingChange(event, index)}
                                        placeholder="Enter card heading"
                                        variant="outlined"
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-process-cards-editor-item-field">
                                <LabelledField label="Card Description">
                                    <SimplicityComposerTipTap
                                        richtext={item?.description}
                                        onChange={(value) => handleDescriptionChange(value, index)}
                                    />
                                </LabelledField>
                            </div>
                        </div>
                    </div>
                );
            })}

            {list.length === 0 && (
                <div className="simplicity-process-cards-editor-empty-state">
                    <Typography variant="body2" color="text.secondary">
                        No process cards added yet. Click &quot;Add Process Card&quot; to get started.
                    </Typography>
                </div>
            )}
        </div>
    );
};

ProcessCardsComponentEditor.displayName = "ProcessCardsComponentEditor";
export { ProcessCardsComponentEditor };
